import { forwardRef, createElement, Children } from 'react';
import styled from '@emotion/styled';
import { css } from '@theme-ui/css';
import { createShouldForwardProp } from '@styled-system/should-forward-prop';
import { system, compose } from '@styled-system/core';
import space$1 from '@styled-system/space';
import color from '@styled-system/color';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var config = {
  alignItems: true,
  alignContent: true,
  justifyItems: true,
  justifyContent: true,
  flexWrap: true,
  flexDirection: true,
  // Flex Item
  flex: true,
  flexBasis: true,
  flexGrow: true,
  flexShrink: true
};
var flexbox = system(config);

var isNumber = function isNumber(n) {
  return typeof n === "number" && !isNaN(n);
};

var isWrapped = function isWrapped(flexWrap) {
  return flexWrap === "nowrap" ? false : true;
};

var isColumn = function isColumn(flexDirection) {
  return ["column", "column-reverse"].includes(flexDirection);
};

var isRow = function isRow(flexDirection) {
  return ["row", "row-reverse"].includes(flexDirection);
};

var transformGapOffset = function transformGapOffset(value, scale, props) {
  var themeValue = scale && scale[value] || value;
  var offsetValue = isNumber(themeValue) ? -themeValue / 2 : "calc(-" + themeValue + " / 2)";
  return props.flexWrap && isWrapped(props.flexWrap) ? offsetValue : undefined;
};

var transformGap = function transformGap(value, scale, props, property) {
  var themeValue = scale && scale[value] || value;

  if (props.flexParent && isWrapped(props.flexParent.flexWrap)) {
    return isNumber(themeValue) ? themeValue / 2 : "calc(" + themeValue + " / 2)";
  }
  /* This is disgusting but it works for now,I'll try to come back to it */


  if (props.flexParent && props.index > 0 && (property === "marginTop" && isColumn(props.flexParent.flexDirection) || property === "marginLeft" && isRow(props.flexParent.flexDirection))) {
    return themeValue;
  }

  return undefined;
};

var space = {
  scale: "space",
  // from @styled-system/space
  defaultScale: [0, 4, 8, 16, 32, 64, 128, 256, 512]
};
var config$1 = {
  gapOffset: _extends({
    property: "margin"
  }, space, {
    transform: transformGapOffset
  }),
  gapTop: _extends({
    property: "marginTop"
  }, space, {
    transform: function transform(value, scale, props) {
      return transformGap(value, scale, props, "marginTop");
    }
  }),
  gapRight: _extends({
    property: "marginRight"
  }, space, {
    transform: function transform(value, scale, props) {
      return transformGap(value, scale, props, "marginRight");
    }
  }),
  gapBottom: _extends({
    property: "marginBottom"
  }, space, {
    transform: function transform(value, scale, props) {
      return transformGap(value, scale, props, "marginBottom");
    }
  }),
  gapLeft: _extends({
    property: "marginLeft"
  }, space, {
    transform: function transform(value, scale, props) {
      return transformGap(value, scale, props, "marginLeft");
    }
  }),
  flexChild: {
    property: "flex",
    transform: function transform(value, scale, props) {
      if (props.flexParent && isRow(props.flexParent.flexDirection)) {
        return value || "0 0 auto";
      }

      return value;
    }
  },
  overflow: {
    property: "overflow",
    transform: function transform(value, scale, props) {
      return value && value !== null && isWrapped(props.flexWrap) ? "hidden" : undefined;
    }
  },
  overflowX: {
    property: "overflowX",
    transform: function transform(value, scale, props) {
      return value && value !== null && !isWrapped(props.flexWrap) && isRow(props.flexDirection) ? "auto" : undefined;
    }
  }
};
var flexgap = system(config$1);

var reset = function reset(props) {
  return typeof props.as === "string" && {
    /**
     * Override browser defaults.
     */
    ul: {
      paddingLeft: 0
    },

    /**
     * Ensure any list-items are rendered without bullets.
     * Adding a zero-width space in the content to prevent VoiceOver disable.
     */
    li: {
      listStyleType: "none",
      "&:before": {
        position: "absolute",
        content: '"\\200B"'
      }
    }
  }[props.as] || {};
};

var Box = styled("div", {
  shouldForwardProp: createShouldForwardProp([].concat(space$1.propNames, color.propNames, flexbox.propNames, flexgap.propNames))
})([{
  boxSizing: "border-box",
  margin: 0,
  minWidth: 0
}, function (props) {
  return [props.__css && css(props.__css)(props.theme), props.as && reset(props), props.sx && css(props.sx)(props.theme), props.css];
}, compose(space$1, color, flexbox, flexgap)]);

var determineChild = function determineChild(parent) {
  return typeof parent === "string" ? {
    ul: "li",
    ol: "li",
    span: "span",
    p: "span",
    h1: "span",
    h2: "span",
    h3: "span",
    h4: "span",
    h5: "span",
    h6: "span"
  }[parent] || "div" : parent;
};

var Flex = forwardRef(function (_ref, ref) {
  var _ref$as = _ref.as,
      as = _ref$as === void 0 ? "div" : _ref$as,
      gap = _ref.gap,
      children = _ref.children,
      flex = _ref.flex,
      flexBasis = _ref.flexBasis,
      flexGrow = _ref.flexGrow,
      flexShrink = _ref.flexShrink,
      _ref$flexDirection = _ref.flexDirection,
      flexDirection = _ref$flexDirection === void 0 ? "row" : _ref$flexDirection,
      _ref$flexWrap = _ref.flexWrap,
      flexWrap = _ref$flexWrap === void 0 ? "nowrap" : _ref$flexWrap,
      props = _objectWithoutPropertiesLoose(_ref, ["as", "gap", "children", "flex", "flexBasis", "flexGrow", "flexShrink", "flexDirection", "flexWrap"]);

  return /*#__PURE__*/createElement(Box, _extends({
    ref: ref,
    as: as,
    __css: {
      display: "flex"
    },
    overflow: gap,
    overflowX: gap,
    gapOffset: gap,
    flexDirection: flexDirection,
    flexWrap: flexWrap
  }, props), Children.toArray(children).map(function (child, i) {
    return /*#__PURE__*/createElement(Box, {
      as: determineChild(as),
      __css: {
        position: "relative"
      },
      key: i,
      index: i,
      flexParent: {
        flexWrap: flexWrap,
        flexDirection: flexDirection
      },
      gapTop: gap,
      gapRight: gap,
      gapBottom: gap,
      gapLeft: gap,
      flexChild: flex,
      flexBasis: flexBasis,
      flexGrow: flexGrow,
      flexShrink: flexShrink
    }, child);
  }));
});

var Inline = forwardRef(function (_ref, ref) {
  var _ref$gap = _ref.gap,
      gap = _ref$gap === void 0 ? 3 : _ref$gap,
      _ref$alignItems = _ref.alignItems,
      alignItems = _ref$alignItems === void 0 ? "center" : _ref$alignItems,
      _ref$flexDirection = _ref.flexDirection,
      flexDirection = _ref$flexDirection === void 0 ? "row" : _ref$flexDirection,
      props = _objectWithoutPropertiesLoose(_ref, ["gap", "alignItems", "flexDirection"]);

  return /*#__PURE__*/createElement(Flex, _extends({
    ref: ref,
    alignItems: alignItems,
    gap: gap,
    flexDirection: flexDirection
  }, props));
});

var Stack = forwardRef(function (_ref, ref) {
  var _ref$gap = _ref.gap,
      gap = _ref$gap === void 0 ? 3 : _ref$gap,
      _ref$flexDirection = _ref.flexDirection,
      flexDirection = _ref$flexDirection === void 0 ? "column" : _ref$flexDirection,
      props = _objectWithoutPropertiesLoose(_ref, ["gap", "flexDirection"]);

  return /*#__PURE__*/createElement(Flex, _extends({
    ref: ref,
    gap: gap,
    flexDirection: flexDirection
  }, props));
});

var Wrap = forwardRef(function (_ref, ref) {
  var _ref$alignItems = _ref.alignItems,
      alignItems = _ref$alignItems === void 0 ? "center" : _ref$alignItems,
      _ref$flexDirection = _ref.flexDirection,
      flexDirection = _ref$flexDirection === void 0 ? "row" : _ref$flexDirection,
      _ref$flexWrap = _ref.flexWrap,
      flexWrap = _ref$flexWrap === void 0 ? "wrap" : _ref$flexWrap,
      _ref$gap = _ref.gap,
      gap = _ref$gap === void 0 ? 3 : _ref$gap,
      _ref$justifyContent = _ref.justifyContent,
      justifyContent = _ref$justifyContent === void 0 ? "flex-start" : _ref$justifyContent,
      props = _objectWithoutPropertiesLoose(_ref, ["alignItems", "flexDirection", "flexWrap", "gap", "justifyContent"]);

  return /*#__PURE__*/createElement(Flex, _extends({
    ref: ref,
    alignItems: alignItems,
    flexDirection: flexDirection,
    flexWrap: flexWrap,
    gap: gap,
    justifyContent: justifyContent
  }, props));
});

export { Flex, Inline, Stack, Wrap };
//# sourceMappingURL=index.esm.js.map
